"use client";

/**
 * Compound entry point for the `enrollment` feature surface (spec §13.1).
 *
 * Callers import a single namespace and reach into the named members so
 * they don't accumulate a long list of imports per surface:
 *
 * ```tsx
 * import { Enrollment } from "@/features/enrollment";
 *
 * <Enrollment.Configure open={open} onOpenChange={setOpen} />
 * <Enrollment.Preview jobId={...} sequenceId={...} previewSessionId={...} />
 * <Enrollment.List jobId={...} sequenceId={...} />
 * ```
 *
 * Phase H shipped the Configure mount; Phase I shipped `Preview` — the
 * full-screen route surface that the Configure modal navigates to after
 * creating a preview session. Phase J (this update) adds `List` — the
 * Enrollments tab on the sequence detail page. Each child is its own
 * client component so server pages can import this namespace without
 * pulling Zustand or react-hook-form into the server bundle.
 */

import ConfigureModal from "./configure-modal";
import { EnrollmentList } from "./list";
import { Preview } from "./preview";
import { EnrollmentPreviewDrawer } from "./preview/drawer";

export const Enrollment = {
  Configure: ConfigureModal,
  /**
   * Primary review-and-enroll surface — a wide right-side drawer driven by
   * `usePreviewStore().openDrawer(...)`. Mount once near the sequence page.
   */
  PreviewDrawer: EnrollmentPreviewDrawer,
  /** Back-compat full-screen route surface (same shared body as the drawer). */
  Preview,
  List: EnrollmentList,
};

export default Enrollment;
